import type { BasePrimitiveValue } from "../common/components/computation-types";
import type { PropertySource } from "../common/description";
import {
    MutatingScreenKind,
    PropertyKind,
    getPrimitiveColumns,
    getStringTypeOrStringTypeArrayColumns,
} from "../common/description";
import { defined } from "../support";
import { imageProperties, isImageType } from "./description-utils";
import type { SuperPropertySection } from "./property-sections";
import { PropertySection } from "./property-sections";
import type {
    ColumnPropertyDescriptorCase,
    ColumnTypePredicate,
    EnumPropertyDescriptorCase,
    GetAllowedColumnsFunction,
    NumberPropertyDescriptorCase,
    PropertyDescriptor,
    PropertyLabel,
    PropertyTableGetter,
    SpecialValuePropertyDescriptorCase,
    StringPropertyDescriptorCase,
    Subcomponent,
} from ".";

export interface ColumnPropertyDescriptorFlags {
    readonly preferredNames?: readonly string[]; // default: `[]`
    readonly preferredType?: ColumnTypePredicate; // default: `"string"`
    readonly ignoreColumnsFromProperties?: readonly string[]; // default: `[]`
    readonly isDefaultCaption?: boolean; // default: `false`
    readonly editable?: boolean; // default: `true`
    readonly searchable?: boolean; // default: `true`
    readonly isEditedInApp?: boolean; // default: `false`
    readonly applyFormat?: boolean; // default: `true`
    readonly allowUserProfileColumns?: boolean; // default: `true`
    readonly emptyByDefault?: boolean; // default: `false`
    // Don't allow picking values from the row to be added in an Add screen.
    // Only used for default value properties right now, which can't come from
    // the row to be added.
    readonly needsExistingValue?: boolean; // default: `false`
    readonly getAllowedColumns?: GetAllowedColumnsFunction; // default: `getPrimitiveColumns`
    // Only used in properties where the column comes from an indirection, such
    // as with Inline Lists or Relations.
    readonly getIndirectTable?: PropertyTableGetter; // default: `undefined`
    readonly invalidateForNewTable?: boolean; // default: false
}

export interface TextPropertyDescriptorFlags extends ColumnPropertyDescriptorFlags {
    readonly textMenuLabel?: string; // default: `"Custom"`
    readonly isCaption?: string; // default: `undefined`
    readonly isMultiLine?: boolean; // default: `false`
    readonly isImageURL?: boolean; // default: `false`
    readonly propertySection?: SuperPropertySection; // default: `PropertySection.Data`
    readonly subcomponent?: Subcomponent;
    // Only used for legacy properties
    readonly columnFirst?: boolean; // default: `false`
    readonly defaultValue?: string; // default: `undefined`
    readonly allowColumn?: boolean; // default: `true`
    readonly syntaxMode?: string; // default: `undefined`
}

export function makeColumnCase(
    required: boolean,
    flags: ColumnPropertyDescriptorFlags,
    defaultPreferredType: ColumnTypePredicate
): ColumnPropertyDescriptorCase {
    return {
        kind: PropertyKind.Column,
        required,
        isEditedInApp: flags.isEditedInApp ?? false,
        editable: flags.editable ?? true,
        searchable: flags.searchable ?? true,
        getAllowedColumns: flags.getAllowedColumns ?? getPrimitiveColumns,
        preferredNames: flags.preferredNames ?? [],
        preferredType: flags.preferredType ?? defaultPreferredType,
        isDefaultCaption: flags.isDefaultCaption ?? false,
        applyFormat: flags.applyFormat ?? true,
        emptyByDefault: flags.emptyByDefault ?? false,
        needsExistingValue: flags.needsExistingValue ?? false,
        allowUserProfileColumns: flags.allowUserProfileColumns ?? true,
        ignoreColumnsFromProperties: flags.ignoreColumnsFromProperties ?? [],
        getIndirectTable: flags.getIndirectTable,
        invalidateForNewTable: flags.invalidateForNewTable ?? false,
    };
}

export function makeColumnOrLiteralPropertyDescriptor<P extends BasePrimitiveValue>(
    property: string | PropertySource,
    label: PropertyLabel,
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection: SuperPropertySection,
    subcomponent: Subcomponent | undefined,
    columnFirst: boolean,
    columnCase: ColumnPropertyDescriptorCase | undefined,
    otherCases: readonly (
        | StringPropertyDescriptorCase
        | NumberPropertyDescriptorCase
        | EnumPropertyDescriptorCase<P>
        | SpecialValuePropertyDescriptorCase
    )[]
): PropertyDescriptor | undefined {
    if (typeof property === "string") {
        property = { name: property };
    }

    const propertyDescriptorBase = {
        property,
        label,
        section: propertySection,
        subcomponent,
    };

    // The image property in the Image component needs this clause.  It's edited
    // in the app, so in the Add screen it has to have a column picker, too.
    // FIXME: This is really a hack, where we special-cased the Image component,
    // and then we special-cased the default value case.
    if (
        columnCase === undefined ||
        (columnCase.needsExistingValue !== true &&
            mutatingScreenKind === MutatingScreenKind.AddScreen &&
            columnCase.isEditedInApp !== true)
    ) {
        if (otherCases.length === 0) {
            return undefined;
        }

        if (otherCases.length === 1) {
            const descr: PropertyDescriptor = {
                ...propertyDescriptorBase,
                ...otherCases[0],
            };
            return descr;
        } else {
            return {
                ...propertyDescriptorBase,
                cases: otherCases,
            };
        }
    }

    if (otherCases.length === 0) {
        return {
            ...propertyDescriptorBase,
            ...columnCase,
        };
    } else {
        return {
            ...propertyDescriptorBase,
            cases: columnFirst ? [columnCase, ...otherCases] : [...otherCases, columnCase],
        };
    }
}

export function makeTextPropertyDescriptor(
    property: string | PropertySource,
    label: PropertyLabel,
    placeholder: string,
    required: boolean,
    mutatingScreenKind: MutatingScreenKind | undefined,
    flags: TextPropertyDescriptorFlags,
    otherCases: readonly SpecialValuePropertyDescriptorCase[] = []
): PropertyDescriptor {
    const stringCase: StringPropertyDescriptorCase = {
        kind: PropertyKind.String,
        required,
        isCaption: flags.isCaption,
        placeholder,
        isMultiLine: flags.isMultiLine,
        isImageURL: flags.isImageURL,
        menuLabel: flags.textMenuLabel ?? "Custom",
        defaultValue: flags.defaultValue ?? (flags.emptyByDefault === true ? "" : undefined),
        syntaxMode: flags.syntaxMode,
    };
    return defined(
        makeColumnOrLiteralPropertyDescriptor(
            property,
            label,
            mutatingScreenKind,
            flags.propertySection ?? PropertySection.Data,
            flags.subcomponent,
            flags.columnFirst ?? false,
            flags.allowColumn !== false ? makeColumnCase(required, flags, "string") : undefined,
            [stringCase, ...otherCases]
        )
    );
}
export function makeImagePropertyDescriptor(
    propertyName: string,
    label: string,
    customMenuLabel: string,
    customPlaceholder: string,
    required: boolean,
    emptyByDefault: boolean | undefined,
    isEditedInApp: boolean,
    withEmoji: boolean,
    withArray: boolean,
    getIndirectTable: PropertyTableGetter | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertySection?: SuperPropertySection,
    subcomponent?: Subcomponent,
    invalidateForNewTable?: boolean
): PropertyDescriptor {
    return makeTextPropertyDescriptor(propertyName, label, customPlaceholder, required, mutatingScreenKind, {
        textMenuLabel: customMenuLabel,
        isEditedInApp,
        searchable: false,
        isImageURL: true,
        emptyByDefault,
        getAllowedColumns: withArray ? getStringTypeOrStringTypeArrayColumns : undefined,
        preferredNames: imageProperties,
        preferredType: !withEmoji && !withArray ? "image-uri" : t => isImageType(t, withEmoji, withArray),
        columnFirst: true,
        getIndirectTable,
        propertySection,
        subcomponent,
        invalidateForNewTable,
    });
}
